<template>
  <div class="rf-guides rf-main-content-wrapper">
    <RfLimitWarning class="mb-4" />
    <div v-if="showEmpty" class="absolute inset-0 left-60 z-0 flex h-full">
      <div class="m-auto inline-flex flex-col items-center gap-4 pt-10 text-center">
        <span class="text-heading-1 text-beige-90 uppercase">No Guides Found</span>
        <span class="text-heading-2 font-normal!">Looks like you haven't created a guide yet.</span>
        <RfButtonNew
          text="+ New Guide"
          size="large"
          type="main"
          class="mt-2"
          :disabled="userStore.isDisabledWithMessage"
          @click="guideModal.open()"
        />
      </div>
    </div>
    <template v-else>
      <div class="flex min-h-12 items-center justify-between gap-16">
        <div class="flex min-w-px flex-1 items-center gap-8">
          <h1 class="shrink-0 text-xl font-semibold not-italic">All guides</h1>
          <RfTagsGroup
            :filters="filters"
            :filtersLength="filtersLength"
            class="pt-2 pb-1"
            @remove="(filterKey, key) => (removeFilter(filterKey, key), refetch())"
            @clear="((filters = {}), refetch())"
          />
        </div>
        <div class="flex grow-0 items-center gap-4">
          <RfButtonInput
            v-model="searchInputs.inputs.value[GUIDES_SEARCH][appsStore.app.id]"
            text="Search"
            placeholder="Search"
            :icon="RfSearchIcon"
            @update:modelValue="refetch()"
          />
          <RfButtonNew
            text="+ New Guide"
            data-cy="guide--create-guide"
            size="large"
            type="main"
            :disabled="userStore.isDisabledWithMessage"
            @click="guideModal.open()"
          />
        </div>
      </div>
      <RfTable
        :tableData="guidesStore.table"
        :loading="guidesStore.loading"
        :loadingMore="guidesStore.loadingMore"
        class="mt-4"
        :rowSkeleton="RfTableRowSkeleton"
        :rowSkeletonProps="{ engagementCategoriesEnabled }"
        variant="outlined"
        type="guides"
        @loadMore="refetch(true)"
      >
        <template #pretable>
          <span class="mb-0 inline-flex items-stretch gap-4">
            <RfTableSelect
              :selected="selected"
              :tableData="guidesStore.table"
              :timezone="timezone"
              @status="
                status => (
                  (statusUpdate = { status, guide: selectedValue }),
                  nextTick(() => statusModal.show())
                )
              "
              @delete="((deleteItem = valuesSelected), nextTick(() => deleteModal.show()))"
              @edit="openEditGuide(selectedValue)"
              @clone="((cloneItem = selectedValue), nextTick(() => cloneModal.show()))"
              @export="exportMultipleGuidesToCSV([...selected.values()], date.preset?.value)"
              @bulkEdit="bulkEditItem = valuesSelected"
              @selectAll="guidesStore.guides.forEach(v => updateMap('set', v))"
              @deselectAll="guidesStore.guides.forEach(v => updateMap('delete', v))"
              @selectByPreset="selectByPreset"
            />
          </span>
          <span class="my-2 inline-flex items-center gap-4">
            <RfDatePicker
              ref="datePicker"
              :min="dayjs().subtract(1, 'year')"
              :max="dayjs()"
              @input="v => ((date = v), refetch())"
            />
          </span>
        </template>
        <template #thead>
          <RfTableHeader
            v-model:filters="filters"
            :segments="allSegmentsStore.segments"
            :sort="sort"
            :users="usersStore.currUsersEx"
            :engagementCategories="engagementCategories"
            :engagementCategoriesEnabled="engagementCategoriesEnabled"
            @update:sort="v => ((sort = v), refetch())"
            @update:filters="refetch()"
          />
        </template>
        <template #tbody>
          <RfTableRow
            v-for="guide in guidesStore.guides"
            :key="guide.id"
            v-memo="[
              guide,
              selected.has(guide.id),
              isRemoving(guide.id),
              date.preset?.value,
              timezone,
              engagementCategoriesEnabled,
              userStore.isDisabledWithMessage,
            ]"
            :class="{ 'rf-row-removing': isRemoving(guide.id) }"
            :guide="guide"
            :disabled="userStore.isDisabledWithMessage"
            :timePeriod="date.preset?.value"
            :timezone="timezone"
            :engagementCategoriesEnabled="engagementCategoriesEnabled"
            :selected="selected.has(guide.id)"
            @select="updateMap('set', guide)"
            @deselect="updateMap('delete', guide)"
            @edit="openEditGuide(guide)"
            @delete="((deleteItem = [guide]), nextTick(() => deleteModal.show()))"
            @status="
              status => ((statusUpdate = { status, guide }), nextTick(() => statusModal.show()))
            "
            @clone="((cloneItem = guide), nextTick(() => cloneModal.show()))"
            @filterUser="
              (userId, userName) => (
                addFilter('creator_ids', userId, userName),
                refetch({ tags: true })
              )
            "
          />
        </template>
      </RfTable>
    </template>
    <RfCloneGuideModal
      v-if="cloneItem"
      ref="cloneModal"
      :sequence="cloneItem"
      :isSuperAdmin="userStore.isCurrentSuperAdmin"
      :isCompanyAdmin="userStore.isCurrentAdmin"
      :currApp="appsStore.app"
      :currUser="userStore.currUser"
      @clone="submitClone"
    />
    <RfBaseModal
      v-if="deleteItem"
      ref="deleteModal"
      :message="`${deleteDialogText.body} This cannot be undone.`"
      width="580"
      errorText="Delete"
      @error="submitDelete"
      @close="deleteItem = null"
    >
      <template #title>
        Are you sure you want to delete
        <span class="font-bold whitespace-pre"> {{ deleteDialogText.title }} </span> ?
      </template>
    </RfBaseModal>
    <RfCreateNewGuide ref="newGuideDialogRef" @created="submitGuide" />
    <RfEditModal ref="editGuideModalRef" title="Edit Guide" @confirm="onGuideUpdated" />
    <RfBaseModal
      v-if="statusUpdate.guide"
      ref="statusModal"
      :title="`${capitalize(statusChangeDialogText.title)} Guide`"
      width="580"
      :secondaryText="capitalize(statusChangeDialogText.title)"
      @secondary="submitStatusUpdate"
      @close="statusUpdate = { status: null, guide: null }"
    >
      <template #body>
        <span v-if="Array.isArray(statusUpdate.guide)">
          Are you sure you want to {{ statusChangeDialogText.title }}
          <b>guides({{ statusUpdate.guide.length }})</b>?
          <b>These guides({{ statusUpdate.guide.length }})</b> will
          {{ statusChangeDialogText.body }}
        </span>
        <span v-else>
          Are you sure you want to {{ statusChangeDialogText.title }} this guide? The guide will
          {{ statusChangeDialogText.body }}

          <RfStartStopDatePicker
            v-if="statusUpdate.status === StatusesSimplifiedConstants.ready.type"
            ref="dateRangePicker"
            class="mt-4"
            :dateRange="{
              startDate: dayjs(statusUpdate.guide.start_date).utc().toISOString(),
              endDate: dayjs(statusUpdate.guide.end_date).utc().toISOString(),
            }"
            :model="statusUpdate.guide"
          />
        </span>
      </template>
    </RfBaseModal>
    <RfBulkEditModal
      ref="bulkEditModal"
      :show="!!bulkEditItem"
      :selectedCount="bulkEditItem?.length || 0"
      :selectedItems="bulkEditItem || []"
      :availableTags="tagsStore.tags"
      :engagementCategories="engagementCategoriesWithRestrictions"
      :engagementCategoriesEnabled="engagementCategoriesEnabled"
      :tagsEnabled="false"
      itemType="guide"
      @apply="submitBulkEdit"
      @close="bulkEditItem = null"
    />
  </div>
</template>

<script setup>
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfButtonInput from "@/components/inputs/RfButtonInput.vue";
import RfSearchIcon from "@/components/icons/RfSearchIcon.vue";
import { useUserSettings } from "@/utils/composables/useUserSettings";
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";
import { useRouter } from "vue-router";
import { GUIDE_TYPES } from "@/utils/constants/GuidesConstants";
import { debounce } from "@/utils/debounce";
import { useGuidesStore } from "@/pinia/guidesStore";
import MetricsUtils from "@/utils/MetricsUtils";
import RfDatePicker from "@/components/inputs/RfDatePicker.vue";
import RfTable from "@/blocks/RfTable/RfTable.vue";
import RfTableHeader from "@/blocks/RfGuides/RfTableHeader.vue";
import RfTableSelect from "@/blocks/RfPrompts/RfTableSelect.vue";
import RfTableRow from "@/blocks/RfGuides/RfTableRow.vue";
import StatusesConstants, {
  SimplifyStatusesMapConstants,
  StatusesSimplifiedConstants,
} from "@/utils/constants/PromptStatusesConstants";
import RfTableRowSkeleton from "@/blocks/RfGuides/RfTableRowSkeleton.vue";
import RfBaseModal from "@/components/modals/RfBaseModal.vue";
import { getGuideLink } from "@/utils/getLink";
import StringUtils from "@/utils/StringUtils";
import getPromptStatus from "@/utils/prompts/getPromptStatus";
import { useAbortable } from "@/utils/composables/useAbortable";
import { useTableFilter } from "@/utils/composables/useTableFilter";
import { useTableSelect } from "@/utils/composables/useTableSelect";
import { useTableSort } from "@/utils/composables/useTableSort";
import { useRowRemoval } from "@/utils/composables/useRowRemoval";
import dayjs from "dayjs";
import { useToastsStore } from "@/pinia/toastsStore";
import RfEditModal from "@/components/modals/RfEditModal.vue";
import { returnFutureEndDate } from "@/utils/TimeUtils";
import { exportMultipleGuidesToCSV } from "@/utils/CsvUtils";
import RfStartStopDatePicker from "@/components/RfStartStopDatePicker.vue";
import { GUIDES_SEARCH, useSearchInputs } from "@/utils/composables/useSearchInputs";
import { useUsersStore } from "@/pinia/usersStore";
import { getUserName } from "@/utils/stringHelpers";
import { useSequencesStore } from "@/pinia/sequencesStore";
import { useTagsStore } from "@/pinia/tagsStore";
import { useAllSegmentsStore } from "@/pinia/allSegmentsStore";
import { useAppsStore } from "@/pinia/appsStore";
import { useUserStore } from "@/pinia/userStore";
import RfTagsGroup from "@/blocks/RfTagsGroup.vue";
import RfCloneGuideModal from "@/blocks/RfModals/RfCloneGuideModal.vue";
import { DEFAULT_END_DATE_DAYJS } from "@/utils/DateDisplayUtils";
import RfLimitWarning from "@/components/RfCommonCards/RfLimitWarning.vue";
import { useEngagementCategories } from "@/utils/composables/useEngagementCategories";
import RfBulkEditModal from "@/blocks/RfModals/RfBulkEditModal.vue";
import RfCreateNewGuide from "@/blocks/RfModals/RfCreateNewGuide.vue";

defineOptions({ name: "RfGuides" });

const debounceTime = 1000;

const router = useRouter();
const guidesStore = useGuidesStore();
const appsStore = useAppsStore();
const userStore = useUserStore();
const toastsStore = useToastsStore();
const usersStore = useUsersStore();
const tagsStore = useTagsStore();
const allSegmentsStore = useAllSegmentsStore();
const sequencesStore = useSequencesStore();
const { getUserSetting, setUserSettings } = useUserSettings();

const showEmpty = ref(false);
const date = ref({});
const statusUpdate = ref({ status: null, guide: null });
const { filters, addFilter, removeFilter, filtersLength, filtersConverted } = useTableFilter();
const { selected, updateMap, resetSelected, selectedValue, valuesSelected } = useTableSelect();
const sort = useTableSort(["start_date", "end_date", "default"]);
const { isRemoving, removeWithFade } = useRowRemoval();
const deleteItem = ref(null);
const cloneItem = ref(null);
const bulkEditItem = ref(null);
const searchInputs = useSearchInputs();

const datePicker = useTemplateRef("datePicker");
const cloneModal = useTemplateRef("cloneModal");
const deleteModal = useTemplateRef("deleteModal");
const statusModal = useTemplateRef("statusModal");
const guideModal = useTemplateRef("newGuideDialogRef");
const editGuideModalRef = useTemplateRef("editGuideModalRef");
const dateRangePicker = useTemplateRef("dateRangePicker");
const bulkEditModalRef = useTemplateRef("bulkEditModal");

const { engagementCategoriesEnabled, engagementCategories, engagementCategoriesWithRestrictions } =
  useEngagementCategories();

watch(
  [
    filtersLength,
    () => guidesStore.guides.length,
    () => guidesStore.loading,
    () => searchInputs.inputs.value[GUIDES_SEARCH],
  ],
  debounce(([filtersLengthIn, guidesLength, guidesLoading, searchInput]) => {
    showEmpty.value = !filtersLengthIn && !guidesLength && !guidesLoading && !searchInput;
  }, debounceTime + 100),
);

const capitalize = StringUtils.capitalize;

const timezone = computed(() => appsStore.app.timezone_offset);

const deleteDialogText = computed(() => {
  const items = deleteItem.value;
  if (!items?.length) return { title: "", body: "" };
  if (items.length > 1)
    return {
      title: `${items.length} guides`,
      body: `Deleting this guides(${items.length}) removes all information about these guides(${items.length}).`,
    };
  return {
    title: items[0].name,
    body: "Deleting this guide removes all information about this guide.",
  };
});

const guideStatus = computed(() => {
  const guide = statusUpdate.value.guide;
  if (!guide) return null;
  const target = Array.isArray(guide) ? guide[0] : guide;
  return SimplifyStatusesMapConstants[getPromptStatus(target, timezone.value).type].type;
});

const statusChangeDialogText = computed(() => {
  const status = statusUpdate.value.status;
  if (status === StatusesSimplifiedConstants.running.type)
    return {
      title: guideStatus.value === StatusesSimplifiedConstants.paused.type ? "resume" : "start",
      body: "run immediately.",
    };
  if (status === StatusesSimplifiedConstants.paused.type)
    return { title: "pause", body: "run on the scheduled start date." };
  if (status === StatusesSimplifiedConstants.ended.type)
    return { title: "end", body: "stop running immediately." };
  if (status === StatusesSimplifiedConstants.ready.type)
    return { title: "schedule", body: "run on start date." };
  return { title: "", body: "" };
});

const getGuides = async (next = false) => {
  if (!date.value.preset) return;
  const statuses = filtersConverted.value.statuses?.string;
  const segments_ids = filtersConverted.value.segments?.string;
  const sequence_type = filtersConverted.value.sequence_type?.string;
  const creator_ids = filtersConverted.value.creator_ids?.string;
  const engagement_category = filtersConverted.value.engagement_category?.string;
  searchInputs.write(
    GUIDES_SEARCH,
    appsStore.app.id,
    searchInputs.inputs.value[GUIDES_SEARCH][appsStore.app.id],
  );

  const result = await abortableFetch(signal =>
    guidesStore.getGuidesPaged(
      appsStore.app.id,
      {
        metric_periods: [
          {
            period: date.value.preset.value,
            ...(date.value.preset.value === "custom" && {
              date_range: [
                date.value.startDate.format("YYYY-MM-DD"),
                date.value.endDate.format("YYYY-MM-DD"),
              ],
            }),
          },
        ],
        ...(searchInputs.inputs.value[GUIDES_SEARCH][appsStore.app.id] && {
          name_search: searchInputs.inputs.value[GUIDES_SEARCH][appsStore.app.id],
        }),
        ...(statuses && { statuses }),
        ...(segments_ids && { segments_ids }),
        ...(sequence_type && { sequence_type }),
        ...(sort.value.string && { order: sort.value.string }),
        ...(creator_ids && { creator_ids }),
        ...(engagement_category && { engagement_category }),
      },
      next,
      { signal },
    ),
  );

  // Bail when aborted or upstream error — don't persist settings from a
  // request that never made it to the user.
  if (result === null) return;

  await setUserSettings({
    guidesSort: sort.value.string,
    guidesStatusFilter: filtersConverted.value.statuses?.array,
    guidesSegmentFilter: filtersConverted.value.segments?.array,
    guidesCreatorsFilter: filtersConverted.value.creator_ids?.array,
    guidesEngagementCategoryFilter: filtersConverted.value.engagement_category?.array,
    guidesType: filtersConverted.value.sequence_type?.array,
    ...(date.value.preset.value !== "custom" ? { guidesDateRange: date.value.preset.value } : {}),
  });
};

const abortableFetch = useAbortable();
const refetch = debounce(async (next = false) => {
  if (!next) {
    resetSelected();
    guidesStore.resetPage();
  }
  await getGuides(next);
}, debounceTime);

const editGuide = guides => {
  resetSelected();
  return guidesStore.bulkUpdate(appsStore.app.id, guides);
};

const updateSegment = guides => {
  editGuide(guides)
    .then(() => toastsStore.create({ type: "success", body: "Guide updated" }))
    .catch(() => null);
};

const editingGuide = ref(null);

const openEditGuide = guide => {
  editingGuide.value = guide;
  editGuideModalRef.value?.open({ name: guide.name, description: guide.description });
};

const onGuideUpdated = ({ name, description }) =>
  updateSegment([{ id: editingGuide.value.id, name, description }]);

const submitGuide = async ({
  name,
  description,
  sequence_type,
  segments,
  engagement_category,
  engagement_subcategory,
  template_key,
}) => {
  let guideId;
  if (template_key) {
    guideId = (
      await sequencesStore.createSequenceFromTemplate({
        appId: appsStore.app.id,
        newSequence: { name, description, template_key, segments },
      })
    )?.id;
  } else {
    guideId = (
      await sequencesStore.createSequence({
        appId: appsStore.app.id,
        newSequence: {
          name,
          description,
          sequence_type,
          segments,
          engagement_category,
          engagement_subcategory,
        },
      })
    )?.id;
  }
  if (!guideId) {
    toastsStore.create({ type: "error", body: "Failed to create guide" });
    return;
  }
  router.replace({ path: getGuideLink(guideId, appsStore.app.id) });
  resetSelected();
};

const submitClone = async ({
  targetAppId,
  targetAppName,
  defaultCompany,
  engagementCategory = null,
}) => {
  let body = `Cloned ${cloneItem.value.name}`;
  let timeout;

  try {
    const sequence = await sequencesStore.cloneSequence({
      appId: appsStore.app.id,
      sequenceId: cloneItem.value.id,
      targetAppId,
      defaultCompany,
      engagementCategory,
    });

    if (appsStore.app.id === targetAppId) {
      if (sequence?.id) {
        const link = getGuideLink(sequence.id, appsStore.app.id);
        router.push(link);
      }
    } else {
      body += ` to ${targetAppName}`;
      timeout = 5000;
    }
    toastsStore.create({ type: "success", body, timeout });
  } catch (error) {
    toastsStore.create({ type: "error", body: error.message });
  }
  cloneModal.value?.close();
  resetSelected();
};

const submitStatusUpdate = () => {
  const item = {
    is_enabled: [
      StatusesSimplifiedConstants.running.type,
      StatusesSimplifiedConstants.ready.type,
    ].includes(statusUpdate.value.status),
  };

  const runningPausedSwitch =
    (statusUpdate.value.status === StatusesSimplifiedConstants.running.type &&
      guideStatus.value !== StatusesSimplifiedConstants.paused.type) ||
    (statusUpdate.value.status === StatusesSimplifiedConstants.paused.type &&
      guideStatus.value === StatusesSimplifiedConstants.ended.type);

  if (runningPausedSwitch) {
    item.start_date = dayjs().format("YYYY-MM-DD");
    item.end_date = DEFAULT_END_DATE_DAYJS.format("YYYY-MM-DD");
    item.is_expired = false;
  }
  if (statusUpdate.value.status === StatusesSimplifiedConstants.ended.type) {
    item.end_date = dayjs().subtract(1, "days").format("YYYY-MM-DD");
    item.is_expired = true;
  }

  if (statusUpdate.value.status === StatusesSimplifiedConstants.ready.type) {
    if (dateRangePicker.value) {
      const { fullStartDate, fullEndDate } = dateRangePicker.value;
      if (dayjs(fullStartDate) >= dayjs(fullEndDate))
        throw new Error("Start date must be before end date");

      item.start_date = fullStartDate;
      item.end_date = fullEndDate;
    }
  }

  const paths = Array.isArray(statusUpdate.value.guide)
    ? statusUpdate.value.guide.map(({ id, end_date }) => ({
        ...item,
        id,
        end_date: runningPausedSwitch
          ? returnFutureEndDate(end_date, item.end_date)
          : item.end_date,
      }))
    : [
        {
          ...item,
          id: statusUpdate.value.guide.id,
          end_date: runningPausedSwitch
            ? returnFutureEndDate(statusUpdate.value.guide.end_date, item.end_date)
            : item.end_date,
        },
      ];
  editGuide(paths)
    .then(() => {
      statusModal.value?.close();
      toastsStore.create({ type: "success", body: "Status updated" });
    })
    .catch(() => null);
};

const selectByPreset = preset => {
  resetSelected();
  guidesStore.guides.forEach(guide => {
    SimplifyStatusesMapConstants[getPromptStatus(guide, timezone.value).type].type === preset &&
      updateMap("set", guide);
  });
};

const submitDelete = async () => {
  const items = deleteItem.value;
  const body = `Guide${items.length > 1 ? "s" : ""} deleted`;

  try {
    await removeWithFade(
      items.map(g => g.id),
      async () => {
        await guidesStore.bulkDelete(appsStore.app.id, items);
        resetSelected();
        await guidesStore.reloadAllLoaded(() => getGuides());
      },
    );
    deleteModal.value?.close();
    toastsStore.create({ type: "success", body });
  } catch (e) {
    toastsStore.create({ type: "error", body: e.message });
  }
};

const submitBulkEdit = async ({ engagement_category, engagement_subcategory }) => {
  const bulkEditModal = bulkEditModalRef.value;
  const guideIds = bulkEditItem.value.map(g => g.id);

  try {
    if (engagement_category !== undefined) {
      const categoryUpdates = guideIds.map(id => ({
        id,
        engagement_category,
        ...(engagement_subcategory !== undefined && { engagement_subcategory }),
      }));
      await guidesStore.bulkUpdate(appsStore.app.id, categoryUpdates);
    }

    bulkEditModal?.reset();
    bulkEditItem.value = null;
    resetSelected();
    await refetch();

    toastsStore.create({
      type: "success",
      body: `${guideIds.length} guide${guideIds.length > 1 ? "s" : ""} edited`,
    });
  } catch (error) {
    if (bulkEditModal) bulkEditModal.isProcessing.value = false;

    toastsStore.create({
      type: "error",
      body:
        error.status === 403
          ? "You don't have permission to edit guides"
          : error.message || "Failed to edit guides",
    });
  }
};

onMounted(async () => {
  sort.value = getUserSetting("guidesSort");

  await Promise.all([
    ...(allSegmentsStore.segments.length
      ? []
      : [allSegmentsStore.getSegments({ appId: appsStore.app.id })]),
    ...(tagsStore.tags?.length ? [] : [tagsStore.getTags(appsStore.app.id)]),
    usersStore.getUsers(),
  ]);

  filters.value = {
    statuses: Object.fromEntries(
      (getUserSetting("guidesStatusFilter") || []).reduce((acc, el) => {
        const status = StatusesConstants[el];
        if (status) acc.push([status.type, status.title]);
        return acc;
      }, []),
    ),
    sequence_type: Object.fromEntries(
      (getUserSetting("guidesType") || []).reduce((acc, el) => {
        const type = GUIDE_TYPES[el];
        if (type) acc.push([type.value, type.label]);
        return acc;
      }, []),
    ),
    segments: Object.fromEntries(
      (getUserSetting("guidesSegmentFilter") || []).reduce((acc, el) => {
        const segment = allSegmentsStore.segments.find(({ key }) => key === el);
        if (segment) acc.push([segment.key, segment.name]);
        return acc;
      }, []),
    ),
    creator_ids: Object.fromEntries(
      (getUserSetting("guidesCreatorsFilter") || []).reduce((acc, el) => {
        const user = usersStore.users.find(({ id }) => id === el);
        if (user) acc.push([user.id, getUserName(user)]);
        return acc;
      }, []),
    ),
    engagement_category: Object.fromEntries(
      (getUserSetting("guidesEngagementCategoryFilter") || []).reduce((acc, el) => {
        const category = engagementCategories.value.find(({ value }) => value === el);
        if (category) acc.push([category.value, category.label]);
        return acc;
      }, []),
    ),
  };

  nextTick(() => {
    const preset = MetricsUtils.All_Periods.find(
      ({ value }) => value !== "since_start_date" && value === getUserSetting("guidesDateRange"),
    )?.value;

    datePicker.value?.setTimeFromPreset(preset || "last_seven_days");
  });
});
</script>
